import { Modal, ModalDialog, ModalOverflow, Link as LinkMui, Typography, Stack, Box, Button, Card, CardContent, Chip, CircularProgress, Alert, DialogTitle, DialogContent, DialogActions, useColorScheme, ModalClose } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useContext } from "react";

import { ArrowLeft, CreditCard, ArrowClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

import { APIGetRates, APICreateInvoice, APICheckInvoiceStatus, APICancelInvoice } from "../../api";
import { LayoutContext } from "../../context";


export const TARIFF_DAYS = 30;
export const FREE_TESTS_PER_DAY = 15;


const PaymentModal = ({ open, onClose, onPaymentSuccess }) => {
  const [step, setStep] = useState('rates');
  const [selectedRate, setSelectedRate] = useState(null);
  const [rates, setRates] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [cancelingPayment, setCancelingPayment] = useState(false);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { token, user, setSuccessPaymentModalOpen } = useContext(LayoutContext);

  // Загрузка тарифов при открытии модального окна
  useEffect(() => {
    if (open) {
      loadRates();
      resetModal();
    }
  }, [open]);

  const resetModal = () => {
    setStep('rates');
    setSelectedRate(null);
    setInvoice(null);
    setError('');
    setShowCancelConfirm(false);
  };

  const loadRates = async () => {
    try {
      setLoading(true);
      const response = await APIGetRates(token);
      if (response.success) {
        setRates(response.rates);
      }
    } catch (error) {
      setError('Ошибка загрузки тарифов');
    } finally {
      setLoading(false);
    }
  };

  const handleRateSelect = (rate) => {
    setSelectedRate(rate);
  };

  function collectAllStrings(obj) {
    const result = [];
    
    function traverse(current) {
        if (typeof current === 'string') {
            result.push(current);
        } else if (Array.isArray(current)) {
            current.forEach(item => traverse(item));
        } else if (typeof current === 'object' && current !== null) {
            Object.values(current).forEach(value => traverse(value));
        }
    }
    
    traverse(obj);
    return result;
  }

  const handleContinue = async () => {
    if (!selectedRate) return;

    try {
      setLoading(true);
      setError('');
      
      const response = await APICreateInvoice(token, selectedRate.id);
      
      if (response.success) {
        setInvoice(response.invoice);
        setStep('payment');
        
        // Для платных тарифов открываем страницу оплаты
        if (response.payment_url) {
          window.open(response.payment_url, '_blank');
        } else {
          // Для бесплатного тарифа сразу переходим к успеху
          setStep('success');
          if (onPaymentSuccess) {
            onPaymentSuccess();
          }
        }
      } else {
        const ffff = response.error || collectAllStrings(response.details).join(', ');
        setError(ffff || 'Ошибка создания платежа');
      }
    } catch (error) {
      console.log("error", error);
      const ffff = collectAllStrings(error.response.data.details).join(', ') || error.response.data.error;
      setError(ffff || 'Ошибка создания платежа');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckPayment = async () => {
    if (!invoice) return;

    try {
      setCheckingPayment(true);
      setError('');
      
      const response = await APICheckInvoiceStatus(token, invoice.uuid_pay);
      
      if (response.success) {
        setInvoice(response.invoice);
        
        if (response.invoice.status_pay === 'paid') {
          setStep('success');
          if (onPaymentSuccess) {
            onPaymentSuccess();
          }
        } else if (response.invoice.status_pay === 'cancelled') {
          setError('Платеж был отменен');
          setStep('rates');
        }
      } else {
        setError(response.error || 'Ошибка проверки платежа');
      }
    } catch (error) {
      setError('Ошибка проверки платежа');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleCancelPayment = async () => {
    if (!invoice) return;

    try {
      setCancelingPayment(true);
      setError('');
      
      const response = await APICancelInvoice(token, invoice.uuid_pay);
      
      if (response.success) {
        setInvoice(response.invoice);
        setStep('rates');
        setShowCancelConfirm(false);
      } else {
        setError(response.error || 'Ошибка отмены платежа');
      }
    } catch (error) {
      setError('Ошибка отмены платежа');
    } finally {
      setCancelingPayment(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
    onClose();
    setSuccessPaymentModalOpen(true);
  };


    // Добавьте эти функции в компонент PaymentModal (перед return)
    const handleOpenCancelConfirm = () => {
        setShowCancelConfirm(true);
    };

    const handleCloseCancelConfirm = () => {
        setShowCancelConfirm(false);
    };

    const handleBackToRates = () => {
        setStep('rates');
        setInvoice(null);
        setError('');
    };


    // Добавьте компонент CancelConfirmationDialog перед export default
    const CancelConfirmationDialog = ({ open, onClose, onConfirm, loading }) => (
        <Modal open={open} onClose={onClose}>
            <ModalOverflow>
            <ModalDialog>
                <DialogTitle>Отмена платежа</DialogTitle>
                <DialogContent>
                <Typography>
                    Вы уверены, что хотите отменить платеж? Это действие нельзя будет отменить.
                </Typography>
                </DialogContent>
                <DialogActions>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                >
                    Нет, оставить
                </Button>
                <Button
                    variant="solid"
                    color="danger"
                    onClick={onConfirm}
                    loading={loading}
                    startDecorator={<XCircle size={18} />}
                >
                    Да, отменить
                </Button>
                </DialogActions>
            </ModalDialog>
            </ModalOverflow>
        </Modal>
    );


  return (
    <AnimatePresence>
      {open && (
        <Modal open={open} sx={{ zIndex: 10001 }}>
          <ModalOverflow>
            <ModalDialog
              component={motion.div}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              sx={{
                maxWidth: 500,
                width: '90%',
                maxHeight: '80vh',
                p: 3,
                borderRadius: 'xl'
              }}
            >
              <ModalClose onClick={onClose}/>

              {error && (
                <Alert color="danger" variant="soft" sx={{ my: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Шаги модального окна */}
              {step === 'rates' && (
                <RatesStep 
                  rates={rates}
                  selectedRate={selectedRate}
                  onRateSelect={handleRateSelect}
                  onContinue={handleContinue}
                  loading={loading}
                  currentPlan={user?.plan}
                />
              )}
              
              {step === 'payment' && (
                <PaymentStep 
                  invoice={invoice}
                  selectedRate={selectedRate}
                  onCheckPayment={handleCheckPayment}
                  onBack={handleBackToRates}
                  onCancel={handleOpenCancelConfirm}
                  checkingPayment={checkingPayment}
                />
              )}
              
              {step === 'success' && (
                <SuccessStep 
                  selectedRate={selectedRate}
                  invoice={invoice}
                  onClose={handlePaymentSuccess}
                />
              )}

              <CancelConfirmationDialog
                open={showCancelConfirm}
                onClose={handleCloseCancelConfirm}
                onConfirm={handleCancelPayment}
                loading={cancelingPayment}
              />
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
};

// Компонент шага выбора тарифа с информацией о лимитах
const RatesStep = ({ rates, selectedRate, onRateSelect, onContinue, loading, currentPlan }) => {
  const { mode } = useColorScheme();

  return (
    <>
      <Typography level="h3" sx={{ textAlign: 'center', mb: 3 }}>
        Выберите тариф
      </Typography>

      {/* Информация о текущем плане */}
      {currentPlan && (
        <Alert color="neutral" sx={{ mb: 2 }}>
          <Typography level="body-sm">
            Текущий план: <strong>{currentPlan === 'free' ? 'Бесплатный' : 'Премиум'}</strong>
          </Typography>
          {currentPlan === 'free' && (
            <Typography level="body-xs">
              Лимит: {FREE_TESTS_PER_DAY} тестов в день
            </Typography>
          )}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {rates.map((rate, index) => (
            <motion.div key={rate.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
              <Card
                variant={selectedRate?.id === rate.id ? "outlined" : "soft"}
                sx={{
                  cursor: 'pointer',
                  borderColor: selectedRate?.id === rate.id ? 'primary.500' : 'transparent',
                  borderWidth: 2,
                  borderRadius: 'lg',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.300',
                    transform: 'translateY(-2px)',
                  }
                }}
                onClick={() => onRateSelect(rate)}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="title-lg" sx={{ mb: 1 }}>
                        {rate.name}
                      </Typography>
                      <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                        {rate.description}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip variant="soft" color="primary" size="sm">
                          {rate.days === 0 ? 'Бессрочный' : `${rate.days} дней`}
                        </Chip>
                        {rate.plan_type === 'free' && (
                          <Chip variant="outlined" color="neutral" size="sm">
                            {FREE_TESTS_PER_DAY} тестов/день
                          </Chip>
                        )}
                        {rate.plan_type === 'premium' && (
                          <Chip variant="outlined" color="success" size="sm">
                            Без ограничений
                          </Chip>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Stack>
      )}

      <Button
        size="lg"
        onClick={onContinue}
        disabled={!selectedRate || loading}
        loading={loading}
        sx={{ width: '100%' }}
      >
        Продолжить
      </Button>

      <Typography level="body-xs" sx={{ lineHeight: 1.2, mt: 1, textAlign: 'center', color: 'neutral.500' }}>
        Нажимая кнопку, вы соглашаетесь с условиями оферты
      </Typography>
    </>
  );
};

// Добавьте эту вспомогательную функцию для отображения статуса
const getStatusDisplay = (status) => {
    const statusMap = {
        pending: 'Ожидает оплаты',
        paid: 'Оплачен',
        failed: 'Ошибка оплаты',
        cancelled: 'Отменен'
    };
    return statusMap[status] || status;
};

// Компонент шага оплаты
const PaymentStep = ({ invoice, selectedRate, onCheckPayment, onBack, checkingPayment }) => (
  <>
    <Button
      variant="plain"
      startDecorator={<ArrowLeft size={18} />}
      onClick={onBack}
      sx={{ mb: 2, alignSelf: 'flex-start' }}
    >
      Назад к тарифам
    </Button>

    <Typography level="h3" sx={{ textAlign: 'center', mb: 3 }}>
      Оплата тарифа
    </Typography>

    {selectedRate && (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Тариф:</Typography>
              <Typography fontWeight="bold">{selectedRate.name}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Срок действия:</Typography>
              <Typography>{selectedRate.days} дней</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    )}

    {invoice && (
      <Box sx={{ mb: 3 }}>
        <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
          ID платежа: {invoice.uuid_token}
        </Typography>
        <Typography level="body-sm" color="neutral">
          Статус: {getStatusDisplay(invoice.status)}
        </Typography>
      </Box>
    )}

    <Stack spacing={2}>
      <Button
        size="lg"
        component="a"
        href={invoice?.payment_url || '#'}
        target="_blank"
        startDecorator={<CreditCard size={18} />}
        sx={{ width: '100%' }}
      >
        Перейти к оплате
      </Button>
      
      <Button
        variant="outlined"
        size="lg"
        onClick={onCheckPayment}
        loading={checkingPayment}
        startDecorator={<ArrowClockwise size={18} />}
        sx={{ width: '100%' }}
      >
        Проверить платеж
      </Button>
    </Stack>
  </>
);

// Компонент шага успешной оплаты
const SuccessStep = ({ selectedRate, invoice, onClose }) => (
  <Stack spacing={3} alignItems="center" textAlign="center">
    <CheckCircle size={64} weight="fill" color="var(--joy-palette-success-500)" />
    
    <Box>
      <Typography level="h3" color="success" sx={{ mb: 1 }}>
        Оплата прошла успешно!
      </Typography>
      <Typography level="body-lg" color="neutral">
        Ваш тариф активирован
      </Typography>
    </Box>

    {selectedRate && (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Тариф:</Typography>
              <Typography fontWeight="bold">{selectedRate.name}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>Действует до:</Typography>
              <Typography>
                {invoice?.expiration_date ? new Date(invoice.expiration_date).toLocaleDateString('ru-RU') : 'Не определено'}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    )}

    <Button
      size="lg"
      onClick={onClose}
      sx={{ width: '100%' }}
    >
      Готово
    </Button>
  </Stack>
);

export default PaymentModal;